<template>
	<div>
		<!-- Data List Items Section -->
		<div class="campaigns-section mb-4">
			<div class="section-header">
				<div class="section-header-content">
					<div class="d-flex align-items-center">
						<router-link to="/auto-campaigns/data-lists" class="back-btn me-3" title="Back to Data Lists">
							<i class="fad fa-arrow-left"></i>
						</router-link>
						<div>
							<h2 class="section-title">{{ dataListName }} - Items</h2>
							<p class="section-subtitle">{{ paginationInfo }}</p>
						</div>
					</div>
				</div>
			</div>

			<!-- Search Only -->
			<div class="secondary-tabs-container">
				<div></div>
				<div class="search-input-wrapper">
					<i class="fad fa-search search-icon"></i>
					<input type="text" class="search-input" placeholder="Search items..." v-model="searchTerm" @input="filterItems" />
				</div>
			</div>

			<!-- Items Table with Skeleton Loading -->
			<div v-if="loadingItems || currentPageItems.length > 0" class="campaigns-table">
				<!-- Table Header -->
				<div class="table-header items-header">
					<div class="header-cell name-col">Name</div>
					<div class="header-cell address-col">Address</div>
					<div class="header-cell city-col">City</div>
					<div class="header-cell state-col">State</div>
					<div class="header-cell zip-col">ZIP</div>
					<div class="header-cell county-col">County</div>
					<div class="header-cell email-col">Email</div>
					<div class="header-cell phone-col">Phone</div>
					<div class="header-cell actions-col">Actions</div>
				</div>

				<!-- Loading State -->
				<div v-if="loadingItems" class="table-loading">
					<div v-for="n in 7" :key="n" class="table-row items-row skeleton-row">
						<div class="table-cell name-cell">
							<div class="item-info">
								<div class="skeleton skeleton-avatar"></div>
								<div class="item-details">
									<div class="skeleton skeleton-text skeleton-name"></div>
								</div>
							</div>
						</div>
						<div class="table-cell address-cell">
							<div class="skeleton skeleton-text skeleton-address"></div>
						</div>
						<div class="table-cell city-cell">
							<div class="skeleton skeleton-text skeleton-city"></div>
						</div>
						<div class="table-cell state-cell">
							<div class="skeleton skeleton-text skeleton-state"></div>
						</div>
						<div class="table-cell zip-cell">
							<div class="skeleton skeleton-text skeleton-zip"></div>
						</div>
						<div class="table-cell county-cell">
							<div class="skeleton skeleton-text skeleton-county"></div>
						</div>
						<div class="table-cell email-cell">
							<div class="skeleton skeleton-badge"></div>
						</div>
						<div class="table-cell phone-cell">
							<div class="skeleton skeleton-badge"></div>
						</div>
						<div class="table-cell actions-cell">
							<div class="skeleton skeleton-actions"></div>
						</div>
					</div>
				</div>

				<!-- Items Rows -->
				<div v-else>
					<div v-for="item in currentPageItems" :key="item.id" class="table-row items-row">
						<!-- Name -->
						<div class="table-cell name-cell">
							<div class="item-info">
								<div class="item-avatar">
									{{ getInitials(item.name) }}
								</div>
								<div class="item-details">
									<div class="item-name">{{ item.name }}</div>
								</div>
							</div>
						</div>

						<!-- Address -->
						<div class="table-cell address-cell">
							<div class="address-info">
								<div v-if="!item.address" class="no-address">
									<i class="fad fa-map-marker-alt text-muted"></i>
									<span class="text-muted">No address</span>
								</div>
								<div v-else class="address-with-icon">
									<i class="fad fa-map-marker-alt"></i>
									<span class="item-text">{{ item.address }}</span>
								</div>
							</div>
						</div>

						<!-- City -->
						<div class="table-cell city-cell">
							<span class="item-text">{{ item.city }}</span>
						</div>

						<!-- State -->
						<div class="table-cell state-cell">
							<span class="item-text">{{ item.state }}</span>
						</div>

						<!-- ZIP -->
						<div class="table-cell zip-cell">
							<span class="item-text">{{ item.zip }}</span>
						</div>

						<!-- County -->
						<div class="table-cell county-cell">
							<span class="item-text">{{ item.county }}</span>
						</div>

						<!-- Email -->
						<div class="table-cell email-cell">
							<div class="contact-badges">
								<div v-if="!getEmailsArray(item).length" class="no-contact">
									<i class="fad fa-envelope text-muted"></i>
									<span class="text-muted">No email</span>
								</div>
								<div v-else class="badges-container">
									<div v-for="(email, index) in getEmailsArray(item)" :key="index" class="contact-badge email-badge">
										<i class="fad fa-envelope"></i>
										<span>{{ email }}</span>
									</div>
								</div>
							</div>
						</div>

						<!-- Phone -->
						<div class="table-cell phone-cell">
							<div class="contact-badges">
								<div v-if="!getPhonesArray(item).length" class="no-contact">
									<i class="fad fa-phone text-muted"></i>
									<span class="text-muted">No phone</span>
								</div>
								<div v-else class="badges-container">
									<div v-for="(phone, index) in getPhonesArray(item)" :key="index" class="contact-badge phone-badge">
										<i class="fad fa-phone"></i>
										<span>{{ phone }}</span>
									</div>
								</div>
							</div>
						</div>

						<!-- Actions -->
						<div class="table-cell actions-cell">
							<button type="button" class="action-icon-btn" @click="editItem(item)" title="Edit Item">
								<i class="fad fa-pen"></i>
							</button>
							<button type="button" class="action-icon-btn delete-btn" @click="deleteItem(item)" title="Delete Item">
								<i class="fad fa-trash"></i>
							</button>
						</div>
					</div>
				</div>
			</div>

			<!-- Pagination Controls -->
			<div v-if="!loadingItems && filteredItems.length > 0 && totalPages > 1" class="pagination-container">
				<div class="pagination-info">
					<span>{{ paginationInfo }}</span>
				</div>
				<nav aria-label="Page navigation">
					<ul class="pagination">
						<li class="page-item" :class="{ disabled: currentPage === 1 }">
							<button class="page-link" @click="goToPage(currentPage - 1)" :disabled="currentPage === 1">
								<i class="fad fa-chevron-left"></i>
								Previous
							</button>
						</li>

						<li v-for="page in visiblePages" :key="page" class="page-item" :class="{ active: page === currentPage }">
							<button v-if="page !== '...'" class="page-link" @click="goToPage(page)">
								{{ page }}
							</button>
							<span v-else class="page-link disabled">...</span>
						</li>

						<li class="page-item" :class="{ disabled: currentPage === totalPages }">
							<button class="page-link" @click="goToPage(currentPage + 1)" :disabled="currentPage === totalPages">
								Next
								<i class="fad fa-chevron-right"></i>
							</button>
						</li>
					</ul>
				</nav>
			</div>

			<!-- Empty State -->
			<div v-else-if="filteredItems.length === 0 && !loadingItems" class="empty-state">
				<div class="empty-state-content">
					<div class="empty-state-icon">
						<i class="fad fa-database"></i>
					</div>
					<h3 class="empty-state-title">No Items Found</h3>
					<p class="empty-state-description">
						<span v-if="searchTerm">No items match your search criteria.</span>
						<span v-else>This data list doesn't contain any items yet.</span>
					</p>
				</div>
			</div>
		</div>

		<!-- Edit Item Offcanvas -->
		<div class="offcanvas offcanvas-end" tabindex="-1" id="editItemOffcanvas" aria-labelledby="editItemOffcanvasLabel">
			<div class="offcanvas-header">
				<h5 class="offcanvas-title" id="editItemOffcanvasLabel">Edit Item</h5>
				<button type="button" class="btn-close" data-bs-dismiss="offcanvas" aria-label="Close"></button>
			</div>
			<div class="offcanvas-body">
				<form @submit.prevent="saveItem">
					<div class="mb-3">
						<label for="editItemName" class="form-label">Name</label>
						<input type="text" class="form-control" id="editItemName" v-model="editingItem.name" required placeholder="Enter item name" />
					</div>
					<div class="mb-3">
						<label for="editItemAddress" class="form-label">Address</label>
						<input type="text" class="form-control" id="editItemAddress" v-model="editingItem.address" placeholder="Enter address" />
					</div>
					<div class="row">
						<div class="col-md-6 mb-3">
							<label for="editItemCity" class="form-label">City</label>
							<input type="text" class="form-control" id="editItemCity" v-model="editingItem.city" placeholder="Enter city" />
						</div>
						<div class="col-md-3 mb-3">
							<label for="editItemState" class="form-label">State</label>
							<input type="text" class="form-control" id="editItemState" v-model="editingItem.state" placeholder="State" />
						</div>
						<div class="col-md-3 mb-3">
							<label for="editItemZip" class="form-label">ZIP</label>
							<input type="text" class="form-control" id="editItemZip" v-model="editingItem.zip" placeholder="ZIP" />
						</div>
					</div>
					<div class="mb-3">
						<label for="editItemCounty" class="form-label">County</label>
						<input type="text" class="form-control" id="editItemCounty" v-model="editingItem.county" placeholder="Enter county" />
					</div>
					<div class="row">
						<div class="col-md-6 mb-3">
							<label for="editItemLat" class="form-label">Latitude</label>
							<input type="number" step="any" class="form-control" id="editItemLat" v-model="editingItem.lat" placeholder="Enter latitude" />
						</div>
						<div class="col-md-6 mb-3">
							<label for="editItemLng" class="form-label">Longitude</label>
							<input type="number" step="any" class="form-control" id="editItemLng" v-model="editingItem.lng" placeholder="Enter longitude" />
						</div>
					</div>
					<div class="mb-3">
						<label for="editItemEmails" class="form-label">Emails</label>
						<textarea class="form-control" id="editItemEmails" v-model="editingItem.emailsText" rows="2" placeholder="Enter emails separated by commas"></textarea>
						<div class="form-text">Separate multiple emails with commas</div>
					</div>
					<div class="mb-3">
						<label for="editItemPhones" class="form-label">Phones</label>
						<textarea
							class="form-control"
							id="editItemPhones"
							v-model="editingItem.phonesText"
							rows="2"
							placeholder="Enter phone numbers separated by commas"
						></textarea>
						<div class="form-text">Separate multiple phone numbers with commas</div>
					</div>
					<div class="d-flex gap-2">
						<button type="submit" class="btn btn-primary" :disabled="isSavingItem">
							<i class="fad fa-save me-2"></i>
							{{ isSavingItem ? 'Saving...' : 'Save Changes' }}
						</button>
						<button type="button" class="btn btn-secondary" data-bs-dismiss="offcanvas">
							Cancel
						</button>
					</div>
				</form>
			</div>
		</div>
	</div>
</template>

<script>
import restAPI from '@/api/index.js'
import { Offcanvas } from 'bootstrap'

export default {
	name: 'DataListItems',
	data() {
		return {
			dataListId: null,
			dataListName: 'Data List',
			searchTerm: '',
			items: [],
			filteredItems: [],
			paginatedItems: [],
			currentPage: 1,
			itemsPerPage: 25,
			loadingItems: false,
			editingItem: {
				id: null,
				name: '',
				address: '',
				city: '',
				state: '',
				zip: '',
				county: '',
				emailsText: '',
				phonesText: '',
				lat: '',
				lng: '',
			},
			isSavingItem: false,
			isDeletingItem: false,
			$editItemOffcanvas: null,
		}
	},
	computed: {
		totalPages() {
			return Math.ceil(this.filteredItems.length / this.itemsPerPage)
		},
		startIndex() {
			return (this.currentPage - 1) * this.itemsPerPage
		},
		endIndex() {
			return Math.min(this.startIndex + this.itemsPerPage, this.filteredItems.length)
		},
		currentPageItems() {
			return this.filteredItems.slice(this.startIndex, this.endIndex)
		},
		paginationInfo() {
			if (this.filteredItems.length === 0) return 'No items'
			return `Showing ${this.startIndex + 1}-${this.endIndex} of ${this.filteredItems.length} items`
		},
		visiblePages() {
			const delta = 2
			const range = []
			const rangeWithDots = []

			for (let i = Math.max(2, this.currentPage - delta); i <= Math.min(this.totalPages - 1, this.currentPage + delta); i++) {
				range.push(i)
			}

			if (this.currentPage - delta > 2) {
				rangeWithDots.push(1, '...')
			} else {
				rangeWithDots.push(1)
			}

			rangeWithDots.push(...range)

			if (this.currentPage + delta < this.totalPages - 1) {
				rangeWithDots.push('...', this.totalPages)
			} else {
				rangeWithDots.push(this.totalPages)
			}

			// Remove duplicates and filter out invalid entries
			const uniquePages = [...new Set(rangeWithDots)].filter(page => page <= this.totalPages)

			// If there's only one page, return empty array (pagination won't show)
			return this.totalPages <= 1 ? [] : uniquePages
		},
	},
	async mounted() {
		this.dataListId = this.$route.params.id
		await this.loadDataListInfo()
		await this.loadItems()
		this.filteredItems = [...this.items]
		this.$editItemOffcanvas = new Offcanvas('#editItemOffcanvas')
	},
	methods: {
		async loadDataListInfo() {
			try {
				const response = await restAPI.get(`/campaign-data-lists/${this.dataListId}`)
				this.dataListName = response.data.name || 'Data List'
			} catch (error) {
				console.error('Error loading data list info:', error)
				this.dataListName = 'Data List'
			}
		},
		async loadItems() {
			try {
				this.loadingItems = true

				const response = await restAPI.get(`/campaign-data-lists/${this.dataListId}/items`)
				this.items = response.data
			} catch (error) {
				console.error('Error loading data list items:', error)
				this.$swal.fire({
					icon: 'error',
					title: 'Failed to Load Items',
					text: 'An error occurred while loading the data list items.',
					confirmButtonText: 'Close',
				})
				this.items = []
			} finally {
				this.loadingItems = false
			}
		},
		filterItems() {
			if (!this.searchTerm.trim()) {
				this.filteredItems = [...this.items]
			} else {
				const searchLower = this.searchTerm.toLowerCase()
				this.filteredItems = this.items.filter(
					item =>
						(item.name || '').toLowerCase().includes(searchLower) ||
						(item.address || '').toLowerCase().includes(searchLower) ||
						(item.city || '').toLowerCase().includes(searchLower) ||
						(item.state || '').toLowerCase().includes(searchLower) ||
						(item.zip || '').toLowerCase().includes(searchLower) ||
						(item.county || '').toLowerCase().includes(searchLower) ||
						this.searchInEmails(item, searchLower) ||
						this.searchInPhones(item, searchLower)
				)
			}
			// Reset to first page when filtering
			this.currentPage = 1
		},
		goToPage(page) {
			if (page >= 1 && page <= this.totalPages) {
				this.currentPage = page
			}
		},
		getFormattedEmails(item) {
			if (!item.emails) return ''
			if (Array.isArray(item.emails)) {
				return item.emails.filter(email => email).join(', ')
			}
			return typeof item.emails === 'string' ? item.emails : ''
		},
		getFormattedPhones(item) {
			if (!item.phones) return ''
			if (Array.isArray(item.phones)) {
				return item.phones.filter(phone => phone).join(', ')
			}
			return typeof item.phones === 'string' ? item.phones : ''
		},
		getEmailsArray(item) {
			if (!item.emails) return []
			if (Array.isArray(item.emails)) {
				return item.emails.filter(email => email && email.trim())
			}
			if (typeof item.emails === 'string' && item.emails.trim()) {
				// Split by comma and clean up each email
				return item.emails
					.split(',')
					.map(email => email.trim())
					.filter(email => email.length > 0)
			}
			return []
		},
		getPhonesArray(item) {
			if (!item.phones) return []
			if (Array.isArray(item.phones)) {
				return item.phones.filter(phone => phone && phone.trim())
			}
			if (typeof item.phones === 'string' && item.phones.trim()) {
				// Split by comma and clean up each phone
				return item.phones
					.split(',')
					.map(phone => phone.trim())
					.filter(phone => phone.length > 0)
			}
			return []
		},
		searchInEmails(item, searchTerm) {
			if (!item.emails) return false
			if (Array.isArray(item.emails)) {
				return item.emails.some(email => email && email.toLowerCase().includes(searchTerm))
			}
			return typeof item.emails === 'string' && item.emails.toLowerCase().includes(searchTerm)
		},
		searchInPhones(item, searchTerm) {
			if (!item.phones) return false
			if (Array.isArray(item.phones)) {
				return item.phones.some(phone => phone && phone.toLowerCase().includes(searchTerm))
			}
			return typeof item.phones === 'string' && item.phones.toLowerCase().includes(searchTerm)
		},
		getInitials(name) {
			if (!name) return '?'
			return name
				.split(' ')
				.map(word => word.charAt(0))
				.join('')
				.toUpperCase()
		},
		editItem(item) {
			// Convert emails and phones to text format for editing
			const emailsText = this.getFormattedEmails(item)
			const phonesText = this.getFormattedPhones(item)

			this.editingItem = {
				id: item.id,
				name: item.name || '',
				address: item.address || '',
				city: item.city || '',
				state: item.state || '',
				zip: item.zip || '',
				county: item.county || '',
				emailsText: emailsText,
				phonesText: phonesText,
				lat: item.lat || '',
				lng: item.lng || '',
			}

			// Open the offcanvas
			this.$editItemOffcanvas.show()
		},
		async saveItem() {
			try {
				this.isSavingItem = true

				// Clean up emails and phones text (trim and remove empty entries)
				const cleanEmailsText = this.editingItem.emailsText
					.split(',')
					.map(email => email.trim())
					.filter(email => email.length > 0)
					.join(', ')

				const cleanPhonesText = this.editingItem.phonesText
					.split(',')
					.map(phone => phone.trim())
					.filter(phone => phone.length > 0)
					.join(', ')

				const payload = {
					name: this.editingItem.name,
					address: this.editingItem.address,
					city: this.editingItem.city,
					state: this.editingItem.state,
					zip: this.editingItem.zip,
					county: this.editingItem.county,
					emails: cleanEmailsText,
					phones: cleanPhonesText,
					lat: this.editingItem.lat || null,
					lng: this.editingItem.lng || null,
				}

				const response = await restAPI.put(`/campaign-data-list-items/${this.editingItem.id}`, payload)

				// Update the local item
				const index = this.items.findIndex(item => item.id === this.editingItem.id)
				if (index !== -1) {
					this.items[index] = { ...this.items[index], ...response.data }
					this.filterItems() // Refresh filtered list
				}

				// Close the offcanvas
				this.$editItemOffcanvas.hide()

				// Show success message
				this.$swal.fire({
					icon: 'success',
					title: 'Item Updated',
					text: `${this.editingItem.name} has been updated successfully.`,
					timer: 3000,
					timerProgressBar: true,
				})
			} catch (error) {
				console.error('Error updating item:', error)
				this.$swal.fire({
					icon: 'error',
					title: 'Failed to Update',
					text: 'An error occurred while updating the item.',
					confirmButtonText: 'Close',
				})
			} finally {
				this.isSavingItem = false
			}
		},
		async deleteItem(item) {
			const result = await this.$swal.fire({
				title: 'Are you sure?',
				text: 'This action cannot be undone!',
				icon: 'warning',
				showCancelButton: true,
				confirmButtonColor: '#d33',
				cancelButtonColor: '#3085d6',
				confirmButtonText: 'Yes, delete it!',
				// Override global toast settings for this dialog
				toast: false,
				position: 'center',
				showConfirmButton: true,
				timer: null,
				timerProgressBar: false,
			})
			if (result.isConfirmed) {
				try {
					this.isDeletingItem = true
					await restAPI.delete(`/campaign-data-list-items/${item.id}`)
					this.$swal.fire({
						icon: 'success',
						title: 'Item deleted successfully!',
						timer: 3000,
						showConfirmButton: false,
					})
					// Remove the item from local arrays
					this.items = this.items.filter(i => i.id !== item.id)
					this.filterItems() // Refresh filtered list
				} catch (error) {
					console.error('Error deleting item:', error)
					let errorMessage = 'An unexpected error occurred.'
					if (error.response && error.response.data && error.response.data.message) {
						errorMessage = error.response.data.message
					}
					this.$swal.fire({
						icon: 'error',
						title: 'Failed to delete item',
						text: errorMessage,
					})
				} finally {
					this.isDeletingItem = false
				}
			}
		},
	},
}
</script>

<style scoped>
/* Campaigns Section */
.campaigns-section {
	background: white;
	border-radius: 8px;
	overflow: hidden;
}

.section-header {
	padding: 24px 24px 16px 24px;
}

.section-header-content {
	display: flex;
	justify-content: space-between;
	align-items: flex-start;
	margin-bottom: 8px;
}

.back-btn {
	width: 40px;
	height: 40px;
	display: flex;
	align-items: center;
	justify-content: center;
	background: #ffffff;
	border: 1px solid #e0e1e1;
	border-radius: 50%;
	color: #64748b;
	text-decoration: none;
	transition: all 0.2s ease;
	flex-shrink: 0;
}

.back-btn:hover {
	background: #f8f9fa;
	border-color: #d1d5db;
	color: #475569;
	text-decoration: none;
}

.section-title {
	color: #111827;
	margin: 0 0 4px 0;
}

.section-subtitle {
	font-size: 14px;
	color: #6b7280;
	margin: 0;
}

/* Secondary Tabs Container */
.secondary-tabs-container {
	display: flex;
	justify-content: space-between;
	align-items: center;
	padding: 0 24px 24px 24px;
}

/* Search Input */
.search-input-wrapper {
	position: relative;
	width: 300px;
}

.search-icon {
	position: absolute;
	left: 12px;
	top: 50%;
	transform: translateY(-50%);
	color: #9ca3af;
	font-size: 14px;
}

.search-input {
	width: 100%;
	padding: 8px 12px 8px 36px;
	border: 1px solid #d1d5db;
	border-radius: 6px;
	font-size: 14px;
	outline: none;
	transition: border-color 0.2s ease;
	background: white;
}

.search-input:focus {
	border-color: #3b82f6;
	box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

/* Loading State */
.loading-state {
	padding: 60px 24px;
	text-align: center;
}

.loading-content {
	max-width: 400px;
	margin: 0 auto;
}

/* Items Table */
.campaigns-table {
	border-top: 1px solid #f3f4f6;
}

.items-header {
	display: grid;
	grid-template-columns: 2fr 2fr 1fr 80px 100px 1fr 2fr 2fr 120px;
	gap: 12px;
}

.table-header {
	display: grid;
	gap: 12px;
	padding: 12px 24px;
	background-color: #f9fafb;
	border-bottom: 1px solid #f3f4f6;
}

.header-cell {
	font-size: 12px;
	font-weight: 600;
	color: #6b7280;
	text-transform: uppercase;
	letter-spacing: 0.05em;
}

.items-row {
	display: grid;
	grid-template-columns: 2fr 2fr 1fr 80px 100px 1fr 2fr 2fr 120px;
	gap: 12px;
}

.table-row {
	display: grid;
	gap: 12px;
	align-items: center;
	padding: 16px 24px;
	border-bottom: 1px solid #f9fafb;
	transition: background-color 0.2s ease;
}

.table-row:hover {
	background-color: #f9fafb;
}

.table-row:last-child {
	border-bottom: none;
}

.table-cell {
	font-size: 14px;
	color: #111827;
}

/* Item Info */
.item-info {
	display: flex;
	align-items: center;
	gap: 12px;
}

.item-avatar {
	width: 40px;
	height: 40px;
	border-radius: 50%;
	background-color: #e5e7eb;
	display: flex;
	align-items: center;
	justify-content: center;
	font-size: 14px;
	font-weight: 600;
	color: #374151;
	flex-shrink: 0;
}

.item-details {
	min-width: 0;
}

.item-name {
	font-size: 14px;
	font-weight: 500;
	color: #111827;
	line-height: 1.4;
}

.item-text {
	font-size: 14px;
	color: #6b7280;
	line-height: 1.4;
}

/* Address Info */
.address-info {
	display: flex;
	align-items: center;
}

.address-with-icon {
	display: flex;
	align-items: center;
	gap: 6px;
}

.address-with-icon i {
	color: #f59e0b;
	font-size: 12px;
	flex-shrink: 0;
}

.no-address {
	display: flex;
	align-items: center;
	gap: 4px;
	font-size: 12px;
	color: #9ca3af;
}

.no-address i {
	font-size: 11px;
}

/* Contact Info */
.contact-text {
	color: #6b7280;
	word-break: break-all;
	font-size: 13px;
	line-height: 1.4;
}

/* Contact Badges */
.contact-badges {
	display: flex;
	flex-direction: column;
	gap: 4px;
}

.badges-container {
	display: flex;
	flex-wrap: wrap;
	gap: 4px;
}

.contact-badge {
	display: inline-flex;
	align-items: center;
	gap: 4px;
	padding: 2px 6px;
	border-radius: 12px;
	font-size: 11px;
	font-weight: 500;
	line-height: 1.2;
	max-width: 100%;
}

.email-badge {
	background-color: #dbeafe;
	color: #1e40af;
	border: 1px solid #bfdbfe;
}

.email-badge i {
	color: #3b82f6;
	font-size: 10px;
}

.phone-badge {
	background-color: #dcfce7;
	color: #166534;
	border: 1px solid #bbf7d0;
}

.phone-badge i {
	color: #22c55e;
	font-size: 10px;
}

.contact-badge span {
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}

.no-contact {
	display: flex;
	align-items: center;
	gap: 4px;
	font-size: 12px;
	color: #9ca3af;
}

.no-contact i {
	font-size: 11px;
}

/* Actions Column */
.actions-cell {
	display: flex;
	gap: 4px;
	align-items: center;
	justify-content: center;
}

.action-icon-btn {
	width: 32px;
	height: 32px;
	border: none;
	background: transparent;
	color: #9ca3af;
	cursor: pointer;
	border-radius: 4px;
	display: flex;
	align-items: center;
	justify-content: center;
	font-size: 14px;
	transition: all 0.2s ease;
}

.action-icon-btn:hover {
	background-color: #f3f4f6;
	color: #6b7280;
}

.action-icon-btn.delete-btn:hover {
	background-color: #fee2e2;
	color: #dc2626;
}

/* Empty State */
.empty-state {
	padding: 60px 24px;
	text-align: center;
}

.empty-state-content {
	max-width: 400px;
	margin: 0 auto;
}

.empty-state-icon {
	width: 80px;
	height: 80px;
	border-radius: 50%;
	background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
	display: flex;
	align-items: center;
	justify-content: center;
	margin: 0 auto 24px;
	box-shadow: 0 8px 32px rgba(102, 126, 234, 0.2);
}

.empty-state-icon i {
	font-size: 32px;
	color: white;
}

.empty-state-title {
	font-size: 20px;
	font-weight: 600;
	color: #1f2937;
	margin-bottom: 12px;
	line-height: 1.3;
}

.empty-state-description {
	color: #64748b;
	font-size: 16px;
	line-height: 1.5;
	margin: 0;
}

/* Skeleton Loading Styles */
.table-loading {
	background: white;
}

.skeleton-row {
	background: white;
}

.skeleton-row:hover {
	background: white;
}

.skeleton {
	background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
	background-size: 200% 100%;
	animation: loading 1.5s infinite;
	border-radius: 4px;
}

@keyframes loading {
	0% {
		background-position: 200% 0;
	}
	100% {
		background-position: -200% 0;
	}
}

.skeleton-avatar {
	width: 40px;
	height: 40px;
	border-radius: 50%;
}

.skeleton-text {
	height: 16px;
	border-radius: 4px;
}

.skeleton-name {
	width: 140px;
}

.skeleton-address {
	width: 180px;
}

.skeleton-city {
	width: 100px;
}

.skeleton-state {
	width: 50px;
}

.skeleton-zip {
	width: 70px;
}

.skeleton-county {
	width: 120px;
}

.skeleton-badge {
	width: 80px;
	height: 20px;
	border-radius: 12px;
}

.skeleton-actions {
	width: 70px;
	height: 32px;
	border-radius: 4px;
}

/* Pagination Styles */
.pagination-container {
	display: flex;
	justify-content: space-between;
	align-items: center;
	padding: 24px;
	border-top: 1px solid #f3f4f6;
	background: white;
}

.pagination-info {
	font-size: 14px;
	color: #6b7280;
}

.pagination {
	display: flex;
	list-style: none;
	margin: 0;
	padding: 0;
	gap: 4px;
}

.page-item {
	display: flex;
}

.page-link {
	display: flex;
	align-items: center;
	gap: 6px;
	padding: 8px 12px;
	font-size: 14px;
	font-weight: 500;
	color: #6b7280;
	background: white;
	border: 1px solid #d1d5db;
	border-radius: 6px;
	cursor: pointer;
	transition: all 0.2s ease;
	text-decoration: none;
	min-width: 40px;
	justify-content: center;
}

.page-link:hover:not(:disabled):not(.disabled) {
	background: #f9fafb;
	border-color: #9ca3af;
	color: #374151;
}

.page-link:disabled,
.page-link.disabled {
	color: #d1d5db;
	cursor: not-allowed;
	background: #f9fafb;
}

.page-item.active .page-link {
	background: #3b82f6;
	border-color: #3b82f6;
	color: white;
}

.page-item.active .page-link:hover {
	background: #2563eb;
	border-color: #2563eb;
}

.page-link i {
	font-size: 12px;
}

@media (max-width: 768px) {
	.pagination-container {
		flex-direction: column;
		gap: 16px;
		align-items: center;
	}

	.pagination {
		flex-wrap: wrap;
		justify-content: center;
	}
}
</style>
